import type {
  ProductAttribute,
  ProductAttributeUnion,
  ProductAttributeValueAmountUnit,
} from '@hub/bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';
import type { AttributeDefinitionFragment } from '@hub/generated/graphql';
import type {
  TechnicalDataAttribute,
  TechnicalDataAttributes,
} from '@hub/bearing-hub/ProductDetails/AkeneoProductData/technical-data';
import type { UnitCode } from '@icommerce/units';
import {
  AngleCode,
  getUnitLabel,
  getUnitLabelForUnitSystem,
  LengthCode,
  PressureCode,
  SpeedCode,
  TemperatureCode,
} from '@icommerce/units';
import { BearingHubHelper } from '@hub/libs/bearing-hub.helper';
import type { i18n } from 'i18next';
import type { Intersect } from '@hub/bearing-hub/common/types';
import type { TechnicalData } from '@hub/models/data';
import type { TechnicalDataCategory } from '@hub/common/ProductTabs/tabs/SuitableProductsTab';
import type {
  IbhBearingTolerancesDto,
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { v4 } from 'uuid';
import { IMPERIAL_UNIT_SYSTEM_LOCALES } from '@hub/libs/constants';
import type { FlatBevelData, IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { UnitSystem } from '@igus/kopla-domain';
import { getIcommerceUnitSystemName } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';

export type PartialTechnicalDataRecord = {
  name?: string;
  label?: string;
  attributes?: TechnicalDataAttributes;
  reference_entity_id?: string;
};

type TechnicalDataValues = ProductAttributeValueAmountUnit | string | boolean;

interface TechnicalDataWithValues {
  id: string;
  categoryName: string;
  description: string;
  value: TechnicalDataValues;
  icon?: string;
}

const imperialOnlyAttributes = ['d2_tolerance_housing_hole_min', 'd2_tolerance_housing_hole_max'];
const metricOnlyAttributes = [];

export const getTechnicalDataToleranceAttributes = (
  tolerances: IbhBearingTolerancesDto,
  locale: string,
  unitSystem: UnitSystem,
  i18nObj: i18n
): TechnicalData[] => {
  if (!tolerances || !Object.values(tolerances).length) {
    return [];
  }
  return Object.keys(tolerances)
    .map(key => {
      if (key === 'd1AfterPressFit') {
        return {
          id: v4(),
          description: BearingHubHelper.getResourceWithFallback(
            i18nObj,
            locale,
            'bearing-hub/bearingHub',
            'TECHNICAL_DATA_LABELS.D1_AFTER_PRESS_FIT'
          ),
          value: tolerances[key as keyof IbhBearingTolerancesDto],
        };
      }
      if (key === 'd2HousingBore') {
        const showHousingBore = IMPERIAL_UNIT_SYSTEM_LOCALES.includes(locale) || unitSystem === UnitSystem.imperial;
        return showHousingBore
          ? {
              id: v4(),
              description: BearingHubHelper.getResourceWithFallback(
                i18nObj,
                locale,
                'bearing-hub/bearingHub',
                'TECHNICAL_DATA_LABELS.D2_HOUSING_BORE'
              ),
              value: tolerances[key as keyof IbhBearingTolerancesDto],
            }
          : undefined;
      }
      return { id: v4(), description: key, value: tolerances[key as keyof IbhBearingTolerancesDto] };
    })
    .filter(Boolean);
};

const filterOutUnitSystemSpecificAttributes = (attributeName: string, unitSystem: UnitSystem) =>
  unitSystem === UnitSystem.metric
    ? !imperialOnlyAttributes.includes(attributeName)
    : !metricOnlyAttributes.includes(attributeName);

const getProperValueForUnitSystem = (values: ProductAttributeValueAmountUnit, unitSystem: UnitSystem) => {
  if (typeof values === 'string' || typeof values === 'boolean') {
    return values;
  }

  const getDefaultTextToDisplay = (valuesToUse: ProductAttributeValueAmountUnit): string => {
    const defaultAmount = valuesToUse.amount;
    const defaultUnit = getUnitLabel(valuesToUse.unit);
    return `${defaultAmount} ${defaultUnit}`;
  };

  const getTextToDisplayFromCalculation = (valuesToUse: ProductAttributeValueAmountUnit): string => {
    const amountFromCalculation = Object.values(valuesToUse.calculations)[0];
    const unitFromCalculation = getUnitLabel(Object.keys(valuesToUse.calculations)[0] as UnitCode);
    return `${amountFromCalculation} ${unitFromCalculation}`;
  };

  if (!values.calculations || unitSystem === UnitSystem.metric) {
    return getDefaultTextToDisplay(values);
  }

  // When unit system is imperial use format: <imperial value> <imperial unit> (<metric value> <metric unit>)
  // Data fom Akeneo is inconsistent, so we need ensure that units are imperial
  // example of data  for given imperial unit system:
  // value { unit: 'INCH', amount: 0.5, calculations: { MILLIMETER: 12.7 } }
  // value { unit: 'CELSIUS', amount: 135, calculations: { FAHRENHEIT: 275 } }

  const expectedImperialUnits: UnitCode[] = [
    SpeedCode.FOOT_PER_SECOND,
    LengthCode.INCH,
    TemperatureCode.FAHRENHEIT,
    PressureCode.PSI,
  ];
  const unitIsImperial = expectedImperialUnits.includes(values.unit);
  const textToDisplayFromCalculation = getTextToDisplayFromCalculation(values);

  return `${unitIsImperial ? getDefaultTextToDisplay(values) : textToDisplayFromCalculation} (${
    unitIsImperial ? textToDisplayFromCalculation : getDefaultTextToDisplay(values)
  })`;
};

const sortTechnicalDataCategories = (itemA: TechnicalDataCategory, itemB: TechnicalDataCategory): number => {
  if (itemA.name?.indexOf('dimensions') === 0 && itemB.name?.indexOf('dimensions') !== 0) {
    return -1;
  }
  if (itemB.name?.indexOf('dimensions') === 0 && itemA.name?.indexOf('dimensions') !== 0) {
    return 1;
  }
  if (itemA.name?.indexOf('manufacturing_and_installation_tolerances') === 0) {
    return -1;
  }
  return 0;
};

const getToleranceSectionTo3DParts = (
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  dimensions: IbhDimensions,
  unitSystem: UnitSystem,
  locale: string,
  i18nObj: i18n
): TechnicalData[] | [] => {
  if (!articleData.material.precision || articleData.productionMethod !== IbhProductionMethods.THREEDIMENSIONALPRINT) {
    return [];
  }

  const lengthUnit = getUnitLabelForUnitSystem(LengthCode.MILLIMETER, getIcommerceUnitSystemName(unitSystem));
  const { precision } = articleData.material;
  const { d1 } = dimensions;
  const fractionDigits = unitSystem === UnitSystem.metric ? 2 : 4;

  return [
    {
      description: `d1 ${BearingHubHelper.getResourceWithFallback(i18nObj, locale, 'bearing-hub/bearingHub', 'COMMON.MIN')}`,
      value: `${Number(d1 - precision).toFixed(fractionDigits)} ${lengthUnit}`,
    },
    {
      description: `d1 ${BearingHubHelper.getResourceWithFallback(i18nObj, locale, 'bearing-hub/bearingHub', 'COMMON.MAX')}`,
      value: `${Number(d1 + precision).toFixed(fractionDigits)} ${lengthUnit}`,
    },
  ];
};

const getDimensionsSection = (
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: UnitSystem,
  dimensions: IbhDimensions,
  i18nObj: i18n
): TechnicalDataCategory => {
  const shapeKey = articleData.shape;
  const dimensionsToDisplay = dimensions || articleData.dimensions;
  const flatBevelData: FlatBevelData = {
    'f1.length': articleData?.bevels?.f1?.length,
    'f2.length': articleData?.bevels?.f2?.length,
    'f3.length': articleData?.bevels?.f3?.length,
    'f4.length': articleData?.bevels?.f4?.length,
    'f1.angle': articleData?.bevels?.f1?.angle,
    'f2.angle': articleData?.bevels?.f2?.angle,
    'f3.angle': articleData?.bevels?.f3?.angle,
    'f4.angle': articleData?.bevels?.f4?.angle,
  };
  const lengthUnit = getUnitLabelForUnitSystem(LengthCode.MILLIMETER, getIcommerceUnitSystemName(unitSystem));
  const angleUnit = getUnitLabel(AngleCode.DEGREE);
  const dimensionsDescriptionMap = BearingHubHelper.getDimensionsDescriptionMap(shapeKey, i18nObj, locale);
  const bevelsDescriptionMap = BearingHubHelper.getBevelsDescriptionMap(i18nObj, locale);

  const createAttributes = (
    data: Record<string, number | undefined> | IbhDimensions,
    descriptionMap: Record<string, string>,
    unit: string
  ) =>
    Object.entries(data)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => ({
        description: descriptionMap[key] || key,
        value: `${String(value)} ${key.includes('angle') ? angleUnit : unit}`,
      }));

  const dimensionsAttributes = createAttributes(dimensionsToDisplay, dimensionsDescriptionMap, lengthUnit);
  const bevelAttributes = createAttributes(flatBevelData, bevelsDescriptionMap, lengthUnit);

  return {
    name: 'dimensions',
    label: BearingHubHelper.getResourceWithFallback(i18nObj, locale, 'bearing-hub/bearingHub', 'HEADLINES.DIMENSIONS'),
    attributes: [...dimensionsAttributes, ...bevelAttributes],
  };
};

const getToleranceSection = (
  tolerances: IbhBearingTolerancesDto,
  locale: string,
  unitSystem: UnitSystem,
  i18nObj: i18n
) => ({
  name: 'manufacturing_and_installation_tolerances',
  label: BearingHubHelper.getResourceWithFallback(
    i18nObj,
    locale,
    'bearing-hub/bearingHub',
    'HEADLINES.INSTALLATION_TOLERANCES'
  ),
  attributes: getTechnicalDataToleranceAttributes(tolerances, locale, unitSystem, i18nObj),
});

const getTechnicalDataWithValues = (
  productAttributes: Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
  attributeDefinitions: AttributeDefinitionFragment[],
  technicalData: PartialTechnicalDataRecord[],
  unitSystem: UnitSystem
): TechnicalDataWithValues[] =>
  attributeDefinitions
    .map(attributeDefinition => {
      const attributeValue =
        productAttributes !== null ? productAttributes[attributeDefinition.attribute_id]?.value : undefined;
      const category: PartialTechnicalDataRecord | undefined = technicalData.find(record =>
        record.attributes.some(el => el.id === attributeDefinition.attribute_id)
      );
      const icon = (
        technicalData
          .find(el => el.name === category?.name)
          ?.attributes.find(attribute => attribute.id === attributeDefinition.attribute_id) as TechnicalDataAttribute
      )?.widenUrl;

      return {
        id: attributeDefinition.attribute_id,
        description: attributeDefinition.label,
        value:
          attributeValue && getProperValueForUnitSystem(attributeValue as ProductAttributeValueAmountUnit, unitSystem),
        categoryName: category?.name,
        icon: icon || '',
      };
    })
    .filter(item => item.value !== undefined);

const createTechnicalDataCategories = (
  productAttributes: Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
  attributeDefinitions: AttributeDefinitionFragment[],
  technicalData: PartialTechnicalDataRecord[],
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: UnitSystem,
  dimensions: IbhDimensions,
  i18nObj: i18n
): TechnicalDataCategory[] =>
  technicalData.map(record => {
    if (['dimensions', 'dimensions_properties'].includes(record.name)) {
      return getDimensionsSection(articleData, locale, unitSystem, dimensions, i18nObj);
    }

    if (['tolerances_properties', 'manufacturing_and_installation_tolerances'].includes(record.name)) {
      return getToleranceSection(articleData.tolerances, locale, unitSystem, i18nObj);
    }

    const { name, label } = record;
    const categoryAttributes = getTechnicalDataWithValues(
      productAttributes,
      attributeDefinitions,
      technicalData,
      unitSystem
    )
      .filter(attribute => attribute.categoryName === name)
      .filter(attribute => filterOutUnitSystemSpecificAttributes(attribute.id, unitSystem));
    const attributes: TechnicalData[] = categoryAttributes.map(attribute => ({
      description: attribute.description,
      value: attribute.value as string | boolean,
      icon: attribute.icon,
    }));
    return {
      name,
      label,
      attributes,
    };
  });

const createTechnicalDataCategoriesWithFallbackData = (
  technicalDataRecords: TechnicalDataCategory[],
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: UnitSystem,
  dimensions: IbhDimensions,
  i18nObj: i18n
): TechnicalDataCategory[] =>
  technicalDataRecords
    .map(record => {
      const newRecord = { ...record };

      if (['dimensions', 'dimensions_properties'].includes(newRecord.name)) {
        return getDimensionsSection(articleData, locale, unitSystem, dimensions, i18nObj);
      }

      // FIXME: check if IBD return proper tolerance for 3d printing
      const toleranceRecord = ['tolerances_properties', 'manufacturing_and_installation_tolerances'].includes(
        newRecord.name
      );
      if (toleranceRecord && articleData.productionMethod === IbhProductionMethods.THREEDIMENSIONALPRINT) {
        newRecord.attributes = [
          ...newRecord.attributes,
          ...getToleranceSectionTo3DParts(articleData, dimensions, unitSystem, locale, i18nObj),
        ];
      } else if (toleranceRecord) {
        return getToleranceSection(articleData.tolerances, locale, unitSystem, i18nObj);
      }

      return newRecord;
    })
    .filter(record => record.attributes.length > 0);

export const getTechnicalDataCategories = (
  technicalData: PartialTechnicalDataRecord[],
  productAttributes: Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
  attributeDefinitions: AttributeDefinitionFragment[],
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: UnitSystem,
  dimensions: IbhDimensions,
  isCustom: boolean,
  i18nObj: i18n
): TechnicalDataCategory[] => {
  const technicalDataCategories = createTechnicalDataCategories(
    productAttributes,
    attributeDefinitions,
    technicalData,
    articleData,
    locale,
    unitSystem,
    dimensions,
    i18nObj
  );
  if (isCustom) {
    const technicalDataCategoriesWithFallbackData = createTechnicalDataCategoriesWithFallbackData(
      technicalDataCategories,
      articleData,
      locale,
      unitSystem,
      dimensions,
      i18nObj
    );

    return technicalDataCategoriesWithFallbackData.sort(sortTechnicalDataCategories);
  }

  return technicalDataCategories.sort(sortTechnicalDataCategories);
};

export const getTechnicalDataCategoriesWithUnits = (
  technicalDataCategories: TechnicalDataCategory[],
  isCustom: boolean
) => {
  if (!isCustom) {
    return technicalDataCategories;
  }

  return technicalDataCategories.map(category => {
    if (category?.name && ['dimensions'].includes(category.name)) {
      return {
        ...category,
        attributes: category.attributes.map(el => ({
          ...el,
          value: `${el.value as string}`,
        })),
      };
    }
    return category;
  });
};
